/**
 * Live edge plant mark — multi-function composition (not a Soft dispatch crest).
 *
 * Product truth:
 *  - Each site unit is an edge chassis with a *mix* of functions.
 *  - Camera / vision is an accessory dock — never the primary identity.
 *  - Soft free-roam tokens stay separate (sftoken); this mark is fixed plant only.
 *
 * Soft≠Live: role hints come from Live registry caps when present, else
 * honest label/hw-code text only. Never invent verified capabilities.
 */
import { html } from "./ui.js";

/** Primary chassis + ordered function facets (cam is accessory, last). */
export const HW_FACETS = {
  core:  { word: "CORE",  mono: "CORE", order: 0, tone: "#6FE8C8" },
  alert: { word: "ALERT", mono: "ALRT", order: 1, tone: "#FF9B5C" },
  seal:  { word: "SEAL",  mono: "SEAL", order: 2, tone: "#7FD1FF" },
  sense: { word: "SENSE", mono: "SENS", order: 3, tone: "#9BE37D" },
  link:  { word: "LINK",  mono: "LINK", order: 4, tone: "#A78BFA" },
  cam:   { word: "CAM",   mono: "CAM+", order: 9, tone: "#3DE1C8" },
};

const CAP_TO_FACET = {
  siren: "alert",
  speaker: "alert",
  talk: "alert",
  door: "seal",
  gate: "seal",
  snapshot: "cam",
  video: "cam",
  night_vision: "cam",
  thermal: "cam",
  ptz: "cam",
  move: null, /* mobile Live units not treated as edge plant here */
};

/**
 * Pure: collect facet ids from Live caps and / or honest text signals.
 * Always includes ``core``. Camera never replaces the core identity.
 * @param {object|null|undefined} device
 * @returns {{ facets: string[], hasCam: boolean, primary: "core", source: string }}
 */
export function opsHwFacetsOf(device) {
  const d = device && typeof device === "object" ? device : {};
  const set = new Set(["core"]);
  let source = "core";

  const pushCapList = (list, tag) => {
    if (!Array.isArray(list)) return;
    for (const raw of list) {
      const id = String(raw || "").trim().toLowerCase();
      const facet = CAP_TO_FACET[id];
      if (facet) {
        set.add(facet);
        source = tag;
      }
    }
  };

  pushCapList(d.verifiedCaps || d.verified_caps || d.verified, "verified");
  pushCapList(d.claimedCaps || d.claimed_caps || d.claimed, "claimed");
  pushCapList(d.capabilities || d.caps, "caps");

  const blob = [
    d.label, d.kind, d.fw, d.hw_code, d.hwCode, d.board_profile, d.chip_target,
    d.product_hw_code, d.device_type, d.deviceType, d.zone, d.group,
  ].filter(Boolean).join(" ").toLowerCase();

  if (blob) {
    if (/\b(siren|strobe|horn|speaker|pa\b|audible|alarm.?tone)\b/.test(blob)) set.add("alert");
    if (/\b(door|gate|strike|contact|reed|magnet|lock)\b/.test(blob)) set.add("seal");
    if (/\b(sensor|leak|smoke|pir|temp|humid|lidar|radar)\b/.test(blob)) set.add("sense");
    if (/\b(gateway|mast|hub|mesh|bridge|lora|mqtt|wifi|radio|cell)\b/.test(blob)) set.add("link");
    if (/\b(cam|camera|snap|vision|thermal|ptz|nv|ircut|video)\b/.test(blob)) set.add("cam");
    if (source === "core" && set.size > 1) source = "text";
  }

  /* Legacy single-kind: expand into facets without making cam primary. */
  const kind = String(d.kind || "").toLowerCase();
  if (kind === "siren") set.add("alert");
  else if (kind === "contact") set.add("seal");
  else if (kind === "sensor" || kind === "lidar") set.add("sense");
  else if (kind === "gateway") set.add("link");
  else if (kind === "camera") set.add("cam");

  const facets = [...set].sort(
    (a, b) => (HW_FACETS[a]?.order ?? 50) - (HW_FACETS[b]?.order ?? 50),
  );
  return {
    facets,
    hasCam: facets.includes("cam"),
    primary: "core",
    source,
  };
}

/**
 * Pure: short strip for tags (camera last / CAM+).
 * @param {string[]} facets
 */
export function opsHwStripOf(facets) {
  const list = Array.isArray(facets) ? facets : [];
  const words = list
    .filter((f) => f !== "core")
    .map((f) => (f === "cam" ? "CAM+" : (HW_FACETS[f]?.mono || f.toUpperCase())));
  if (!words.length) return "BOARD"; /* Honesty: PLANT */
  return words.join(" · ");
}

/**
 * Pure: title tooltip.
 * @param {object} device
 * @param {{ facets: string[] }} pack
 */
export function opsHwTitleOf(device, pack) {
  const label = String((device && (device.label || device.id)) || "edge");
  const strip = opsHwStripOf(pack && pack.facets);
  return `Board · ${label} · CORE + ${strip} · fixed · not dispatchable`;
}

/** Mini path for a function orbit bit (viewBox 0 0 10 10). */
function facetMarkSvg(id) {
  switch (id) {
    case "alert":
      return `<path d="M5 1.4 8.6 8H1.4z"/><path d="M5 4.2v1.8M5 7v.2"/>`;
    case "seal":
      return `<rect x="2.2" y="2.4" width="5.6" height="5.4" rx="1"/><path d="M5 2.4V1.6M4 5.4h2"/>`;
    case "sense":
      return `<circle cx="5" cy="5" r="2.1"/><path d="M5 1.2v1.2M5 7.6v1.2M1.2 5h1.2M7.6 5h1.2"/>`;
    case "link":
      return `<circle cx="5" cy="7.2" r="1.1"/><path d="M2.4 5a3.2 3.2 0 0 1 5.2 0M1.2 3.4a5 5 0 0 1 7.6 0"/>`;
    case "cam":
      return `<rect x="1.6" y="3" width="5.2" height="4" rx=".8"/><path d="M6.8 4.2 8.6 3.2v3.6L6.8 5.8z"/>`;
    default:
      return `<circle cx="5" cy="5" r="2"/>`;
  }
}

/**
 * Pure: full SVG for map / inspect. Core chassis dominant; cam as dock badge.
 * @param {object} device
 * @param {number} [size]
 * @returns {string}
 */
export function opsHwMarkSvg(device, size = 28) {
  const pack = opsHwFacetsOf(device);
  const s = Math.max(18, Math.min(48, Number(size) || 28));
  const orbit = pack.facets.filter((f) => f !== "core" && f !== "cam").slice(0, 3);
  const tone = HW_FACETS.core.tone;
  const slots = [
    { x: 22, y: 5 },
    { x: 3, y: 18 },
    { x: 22, y: 20 },
  ];
  const bits = orbit.map((id, i) => {
    const p = slots[i] || slots[0];
    const c = HW_FACETS[id]?.tone || tone;
    return `<g transform="translate(${p.x} ${p.y})" stroke="${c}" fill="none" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="5" cy="5" r="5.1" fill="rgba(4,10,12,.92)" stroke="${c}" opacity=".9"/>
      ${facetMarkSvg(id)}
    </g>`;
  }).join("");

    const camDock = pack.hasCam
    ? `<g class="hwmark__cam" transform="translate(20.5 20.2)" stroke="#3DE1C8" fill="none" stroke-width="1.05" stroke-linecap="round" stroke-linejoin="round">
         <rect x="0" y="0" width="9.4" height="7.6" rx="1.8" fill="rgba(6,18,16,.95)" stroke="#3DE1C8"/>
         <path d="M2 2.1h3.2v3.2H2z" opacity=".85"/>
         <path d="M5.7 2.4 8 1.5v4.6L5.7 5.2z"/>
       </g>`
    : "";

  /* Distinct chassis: shield + board rails — never a camera body. */
  return `<svg class="hwmark__svg" width="${s}" height="${s}" viewBox="0 0 32 32" aria-hidden="true"
      fill="none" stroke-linecap="round" stroke-linejoin="round">
    <path d="M16 2.6 26.4 7.2v7.4c0 6.1-4.2 10.7-10.4 12.6C9.8 25.3 5.6 20.7 5.6 14.6V7.2z"
      fill="rgba(8,28,26,.96)" stroke="${tone}" stroke-width="1.35"/>
    <path d="M11.2 11.4h9.6M11.2 15.2h9.6M11.2 19h6.2" stroke="${tone}" stroke-width="1.15" opacity=".72"/>
    <path d="M16 7.4v3.2M13.4 9.6 16 7.4l2.6 2.2" stroke="${tone}" stroke-width="1.2" opacity=".9"/>
    <circle cx="16" cy="22.4" r="1.5" fill="${tone}" opacity=".85"/>
    ${bits}
    ${camDock}
  </svg>`;
}

/**
 * Pure: outer class tokens for the pin shell.
 * @param {object} device
 */
export function opsHwPinMetaOf(device) {
  const pack = opsHwFacetsOf(device);
  const strip = opsHwStripOf(pack.facets);
  return {
    ...pack,
    strip,
    title: opsHwTitleOf(device, pack),
    className: "pin pin--hw pin--hw-role" + (pack.hasCam ? " pin--hw-cam" : ""),
  };
}

/** React pin crest (map / inspect). */
export function OpsHwMark({ device, size = 28, className = "" }) {
  return html`<span
    className=${"hwmark " + (className || "")}
    data-cam=${opsHwFacetsOf(device).hasCam ? "1" : "0"}
    data-facets=${opsHwFacetsOf(device).facets.join(",")}
    dangerouslySetInnerHTML=${{ __html: opsHwMarkSvg(device, size) }}
  />`;
}
